import { useState, useEffect } from 'react';
import { siteInfo, testimonials } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbItems.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.label,
    item: item.href ? `http://w340q.cn/${item.href}` : undefined,
  })),
};

const stats = [
  { label: '收录作品', value: '12,800+', icon: 'ri-book-2-line' },
  { label: '总章节数', value: '250万+', icon: 'ri-file-list-3-line' },
  { label: '日活跃用户', value: '50万+', icon: 'ri-user-smile-line' },
  { label: '月活跃用户', value: '380万+', icon: 'ri-group-line' },
];

const milestones = [
  { year: '2019', title: '平台创立', desc: '韩国漫画大全正式上线，致力于为中国韩漫爱好者提供免费高质量的韩漫阅读服务。' },
  { year: '2020', title: '收录破千', desc: '平台收录韩漫作品突破1,000部，覆盖恋爱、悬疑、动作等主要类型。' },
  { year: '2021', title: '用户百万', desc: '月活跃用户突破100万，成为国内领先的韩漫阅读平台之一。' },
  { year: '2023', title: '收录破万', desc: '平台收录韩漫作品突破10,000部，涵盖连载与完结全部经典之作。' },
  { year: '2025', title: '全面升级', desc: '网站全面改版升级，优化阅读体验，新增个性化推荐和收藏功能。' },
  { year: '2026', title: '持续成长', desc: '每日更新热门韩漫，持续扩展作品库，为用户提供更优质的阅读体验。' },
];

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于${siteInfo.name}`,
  description: siteInfo.description,
  url: 'http://w340q.cn/about',
  inLanguage: 'zh-CN',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '韩国漫画大全是什么网站？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩国漫画大全是一个免费在线韩漫阅读平台，收录超万部韩漫作品，涵盖恋爱、悬疑、动作、奇幻、科幻等多种类型，每日更新热门连载，所有作品均可免费在线阅读。',
      },
    },
    {
      '@type': 'Question',
      name: '韩国漫画大全的漫画需要付费吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '不需要。韩国漫画大全所有韩漫作品完全免费在线阅读，没有任何隐藏收费，无需注册即可阅读。',
      },
    },
    {
      '@type': 'Question',
      name: '韩国漫画大全多久更新一次？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩国漫画大全每日更新热门韩漫连载，确保用户可以第一时间阅读到最新的漫画章节。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在手机上阅读韩国漫画大全？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '韩国漫画大全网站完美适配手机端浏览器，您只需在手机浏览器中打开网站即可流畅阅读。网站采用响应式设计，自动适配不同屏幕尺寸。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系韩国漫画大全的客服？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `您可以通过邮箱 ${siteInfo.contactEmail} 联系我们，或者在网站上的联系表单中提交您的意见或建议，我们会尽快回复。`,
      },
    },
  ],
};

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    document.title = `关于我们 - 韩国漫画大全 | 免费在线韩漫阅读网`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `关于韩国漫画大全，${siteInfo.description}了解我们的品牌故事、用户口碑评价和联系方式。`);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const hp = formData.get('company_alt');
    if (hp && typeof hp === 'string' && hp.trim() !== '') {
      setFormState('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormState('submitting');
    setFormMessage('');

    try {
      const res = await fetch('http://w340q.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed.code === 'OK') {
        setFormState('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        setFormState('error');
        setFormMessage(parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试');
      }
    } catch {
      setFormState('error');
      setFormMessage('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <Breadcrumb items={breadcrumbItems} />

      <section className="px-4 md:px-6 py-10 md:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950">
              关于<span className="text-accent-500">韩国漫画大全</span>
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center p-5 rounded-xl bg-background-100">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className={`${stat.icon} text-lg`}></i>
                </div>
                <div className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">{stat.value}</div>
                <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-10">
              品牌故事
            </h2>
            <div className="space-y-8">
              {milestones.map((m, index) => (
                <div key={m.year} className={`flex gap-6 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                  <div className="hidden md:block w-24 shrink-0 text-right">
                    <span className="text-2xl font-bold font-heading text-accent-500">{m.year}</span>
                  </div>
                  <div className="relative hidden md:flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-accent-500 shrink-0 mt-1.5"></div>
                    <div className="w-0.5 flex-1 bg-background-300"></div>
                  </div>
                  <div className="flex-1 p-5 rounded-xl bg-background-100">
                    <span className="md:hidden text-sm font-bold text-accent-500">{m.year}</span>
                    <h3 className="text-base font-bold text-foreground-900 mt-1 md:mt-0">{m.title}</h3>
                    <p className="text-sm text-foreground-500 mt-2 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.name} className="p-6 rounded-xl bg-background-100">
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map(i => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="mt-4 pt-4 border-t border-background-200">
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-10">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqSchema.mainEntity.map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-100 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-200/60 transition-colors">
                    <span className="text-sm font-semibold text-foreground-800 pr-4">
                      {faq.name}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
                联系我们
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                    <p className="text-sm text-foreground-500">{siteInfo.contactEmail}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">办公地址</p>
                    <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">工作时间</p>
                    <p className="text-sm text-foreground-500">周一至周五 09:00 - 18:00（韩国标准时间）</p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <p className="text-sm text-foreground-500">
                  我们非常重视每一位用户的反馈。如果您在使用过程中遇到任何问题，或有任何建议，欢迎随时与我们联系。我们会在1-2个工作日内回复您的留言。
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
                留言反馈
              </h2>
              <form
                id="contact-form"
               
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact_email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                    placeholder="留言主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字符</p>
                </div>
                <input
                  type="text"
                  name="company_alt"
                  className="form-hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {formState === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      发送留言
                    </>
                  )}
                </button>
                {formMessage && (
                  <p className={`text-sm text-center p-3 rounded-lg ${
                    formState === 'success'
                      ? 'bg-accent-100/70 text-accent-700'
                      : 'bg-red-100 text-red-600'
                  }`}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}